/**
 * Opora AI Hub — script.js
 * Premium micro-interactions, JTBD routers, search/filter,
 * activity strip, localStorage profile check, sticky mobile CTA
 *
 * Zero dependencies. Vanilla JS. 60fps.
 */

'use strict';

// ─── CONSTANTS ──────────────────────────────────────────────
const PROFILE_KEY   = 'OporaProfile';
const BANNER_KEY    = 'OporaHubBannerDismissed';
const ANALYTICS_KEY = 'OporaHubAnalytics';
const EASE_OUT      = 'cubic-bezier(0.16, 1, 0.3, 1)';

// ─── THEME TOGGLE ────────────────────────────────────────────
(function initTheme() {
  const html   = document.documentElement;
  const toggle = document.querySelector('[data-theme-toggle]');
  let theme    = html.getAttribute('data-theme') ||
                 (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

  function applyTheme(t) {
    html.setAttribute('data-theme', t);
    if (!toggle) return;
    toggle.setAttribute('aria-label', `Switch to ${t === 'dark' ? 'light' : 'dark'} mode`);
    toggle.innerHTML = t === 'dark'
      ? `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>`
      : `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;
  }

  applyTheme(theme);

  if (toggle) {
    toggle.addEventListener('click', () => {
      theme = theme === 'dark' ? 'light' : 'dark';
      applyTheme(theme);
    });
  }
})();


// ─── ANALYTICS LOGGING (localStorage, anonymized) ───────────
function logEvent(action) {
  try {
    const store = JSON.parse(localStorage.getItem(ANALYTICS_KEY) || '{}');
    const today = new Date().toISOString().slice(0, 10);
    if (!store[today]) store[today] = {};
    store[today][action] = (store[today][action] || 0) + 1;
    // Keep only last 30 days
    const keys = Object.keys(store).sort();
    if (keys.length > 30) delete store[keys[0]];
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(store));
  } catch (_) { /* localStorage blocked — silently ignore */ }
}

// Attach logging to all [data-log] elements
document.querySelectorAll('[data-log]').forEach(el => {
  el.addEventListener('click', function () {
    logEvent(this.dataset.log);
  });
});


// ─── HERO ENTRANCE ANIMATIONS ───────────────────────────────
(function heroEntrance() {
  // Trust pills
  document.querySelectorAll('.trust-pill').forEach(pill => {
    const delay = parseInt(pill.dataset.delay || '0', 10);
    setTimeout(() => pill.classList.add('visible'), delay);
  });

  // H1 words
  document.querySelectorAll('.hero__word').forEach(word => {
    const delay = parseInt(word.dataset.delay || '300', 10);
    setTimeout(() => word.classList.add('visible'), delay);
  });

  // Sub
  const sub = document.querySelector('.hero__sub');
  if (sub) {
    const delay = parseInt(sub.dataset.delay || '800', 10);
    setTimeout(() => sub.classList.add('visible'), delay);
  }

  // CTA group
  const ctaGroup = document.querySelector('.hero__cta-group');
  if (ctaGroup) {
    const delay = parseInt(ctaGroup.dataset.delay || '950', 10);
    setTimeout(() => ctaGroup.classList.add('visible'), delay);
  }

  // Hero visual
  const visual = document.querySelector('#hero-visual');
  if (visual) {
    const delay = parseInt(visual.dataset.delay || '600', 10);
    setTimeout(() => visual.classList.add('visible'), delay);
  }

  // Scroll hint
  const hint = document.querySelector('#scroll-hint');
  if (hint) {
    setTimeout(() => hint.classList.add('visible'), 1400);
  }

  // Proof strip (real AI grounding)
  const proofStrip = document.querySelector('.hero__proof-strip');
  if (proofStrip) {
    const delay = parseInt(proofStrip.dataset.delay || '1100', 10);
    setTimeout(() => proofStrip.classList.add('visible'), delay);
  }

  // Live AI typing demo — streams real-feeling Bid Drafter output
  const stream = document.getElementById('hero-ai-stream');
  const cursor = document.getElementById('hero-ai-cursor');
  const cite   = document.getElementById('hero-ai-cite');
  if (stream) {
    let chunks = [];
    try { chunks = JSON.parse(stream.dataset.stream || '[]'); } catch (_) { chunks = []; }
    stream.textContent = '';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      stream.textContent = chunks.join('');
      if (cursor) cursor.classList.add('done');
      if (cite) cite.hidden = false;
    } else {
      // Wait for hero visual to slide in, then type
      setTimeout(function typeStream() {
        let chunkIdx = 0;
        let charIdx = 0;
        function step() {
          if (chunkIdx >= chunks.length) {
            if (cursor) cursor.classList.add('done');
            if (cite) cite.hidden = false;
            return;
          }
          const chunk = chunks[chunkIdx];
          if (charIdx < chunk.length) {
            stream.textContent += chunk[charIdx];
            charIdx++;
            // Variable pacing — faster mid-word, pause on newlines/punctuation
            const ch = chunk[charIdx - 1];
            let delay = 18 + Math.random() * 12;
            if (ch === '\n') delay = 220;
            else if (ch === '.' || ch === '!' || ch === '?') delay = 180;
            else if (ch === ',' || ch === ';') delay = 80;
            setTimeout(step, delay);
          } else {
            chunkIdx++;
            charIdx = 0;
            // Pause between chunks (like model thinking)
            setTimeout(step, 140);
          }
        }
        step();
      }, 1400); // start after hero CTA settles
    }
  }
})();


// ─── SMOOTH SCROLL (hero CTA) ────────────────────────────────
(function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const target = document.querySelector(this.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
})();


// ─── PROFILE BANNER ─────────────────────────────────────────
(function initProfileBanner() {
  const banner   = document.getElementById('profile-banner');
  const dismiss  = document.getElementById('dismiss-profile-banner');
  const openBtn  = document.getElementById('open-profile-modal');
  if (!banner) return;

  let hasProfile = false;
  let dismissed  = false;

  try {
    hasProfile = !!localStorage.getItem(PROFILE_KEY);
    dismissed  = !!localStorage.getItem(BANNER_KEY);
  } catch (_) {}

  if (!hasProfile && !dismissed) {
    // Short delay so hero loads first
    setTimeout(() => {
      banner.removeAttribute('hidden');
      // Animate in
      banner.style.transform = 'translateY(-100%)';
      banner.style.opacity   = '0';
      banner.style.transition = `transform 400ms ${EASE_OUT}, opacity 400ms ${EASE_OUT}`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          banner.style.transform = 'translateY(0)';
          banner.style.opacity   = '1';
        });
      });
    }, 1800);
  }

  if (dismiss) {
    dismiss.addEventListener('click', () => {
      banner.style.transition = 'transform 250ms, opacity 250ms';
      banner.style.transform  = 'translateY(-100%)';
      banner.style.opacity    = '0';
      setTimeout(() => banner.setAttribute('hidden', ''), 260);
      try { localStorage.setItem(BANNER_KEY, '1'); } catch (_) {}
      logEvent('banner-dismissed');
    });
  }

  if (openBtn) {
    openBtn.addEventListener('click', () => {
      logEvent('banner-profile-open');
      // Dispatch event so the prompt-library profile modal can listen
      document.dispatchEvent(new CustomEvent('opora:open-profile-modal'));
      // If no modal listener, navigate to profile page
      setTimeout(() => {
        if (!document.querySelector('.opora-profile-modal[open]')) {
          window.location.href = '/pages/business-profile';
        }
      }, 200);
    });
  }
})();


// ─── JTBD TILE ROUTERS ───────────────────────────────────────
(function initJtbdTiles() {
  const tiles = document.querySelectorAll('.jtbd-tile');

  tiles.forEach(tile => {
    const header = tile.querySelector('.jtbd-tile__header');
    const router = tile.querySelector('.jtbd-router');
    if (!header || !router) return;

    // Height animation helper
    function openRouter() {
      router.removeAttribute('hidden');
      const fullH = router.scrollHeight + 'px';
      router.style.maxHeight = '0';
      router.style.overflow  = 'hidden';
      router.style.transition = `max-height 400ms ${EASE_OUT}`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          router.style.maxHeight = fullH;
        });
      });
      header.setAttribute('aria-expanded', 'true');

      // After animation completes, remove constraint so content reflows freely
      setTimeout(() => {
        router.style.maxHeight = 'none';
        router.style.overflow  = 'visible';
      }, 420);
    }

    function closeRouter() {
      router.style.overflow  = 'hidden';
      router.style.maxHeight = router.scrollHeight + 'px';
      router.style.transition = `max-height 300ms cubic-bezier(0.4, 0, 1, 1)`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          router.style.maxHeight = '0';
        });
      });
      header.setAttribute('aria-expanded', 'false');
      setTimeout(() => router.setAttribute('hidden', ''), 310);
    }

    // Toggle on click
    header.addEventListener('click', () => {
      const expanded = header.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        closeRouter();
      } else {
        // Close others
        tiles.forEach(otherTile => {
          if (otherTile === tile) return;
          const otherHeader = otherTile.querySelector('.jtbd-tile__header');
          const otherRouter = otherTile.querySelector('.jtbd-router');
          if (!otherRouter || otherRouter.hasAttribute('hidden')) return;
          otherRouter.style.overflow  = 'hidden';
          otherRouter.style.maxHeight = otherRouter.scrollHeight + 'px';
          otherRouter.style.transition = `max-height 300ms cubic-bezier(0.4, 0, 1, 1)`;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => { otherRouter.style.maxHeight = '0'; });
          });
          if (otherHeader) otherHeader.setAttribute('aria-expanded', 'false');
          setTimeout(() => otherRouter.setAttribute('hidden', ''), 310);
        });
        openRouter();
        logEvent(header.dataset.log || 'tile-unknown');
      }
    });

    // Keyboard: Enter/Space
    header.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        header.click();
      }
    });
  });
})();


// ─── ROUTER NEXT/BACK BUTTONS ────────────────────────────────
(function initRouterSteps() {
  // "Continue" buttons (data-next)
  document.querySelectorAll('[data-next]').forEach(btn => {
    btn.addEventListener('click', function () {
      const currentStep = this.closest('.router__step');
      const nextId      = this.dataset.next;
      const nextStep    = document.getElementById(nextId);
      if (!nextStep) return;

      // Animate out current
      currentStep.style.transition = `opacity 200ms ${EASE_OUT}, transform 200ms ${EASE_OUT}`;
      currentStep.style.opacity    = '0';
      currentStep.style.transform  = 'translateX(-8px)';

      setTimeout(() => {
        currentStep.setAttribute('hidden', '');
        currentStep.style.opacity   = '';
        currentStep.style.transform = '';
        // Animate in next
        nextStep.removeAttribute('hidden');
        nextStep.style.opacity   = '0';
        nextStep.style.transform = 'translateX(8px)';
        nextStep.style.transition = `opacity 280ms ${EASE_OUT}, transform 280ms ${EASE_OUT}`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            nextStep.style.opacity   = '1';
            nextStep.style.transform = 'translateX(0)';
          });
        });
        logEvent(`router-step-${nextId}`);
        // Recalculate router height
        const router = nextStep.closest('.jtbd-router');
        if (router && router.style.maxHeight !== 'none') {
          router.style.maxHeight = router.scrollHeight + 'px';
        }
      }, 210);
    });
  });

  // "Back" buttons (data-back)
  document.querySelectorAll('[data-back]').forEach(btn => {
    btn.addEventListener('click', function () {
      const currentStep = this.closest('.router__step');
      const prevId      = this.dataset.back;
      const prevStep    = document.getElementById(prevId);
      if (!prevStep) return;

      currentStep.style.transition = `opacity 200ms ${EASE_OUT}, transform 200ms ${EASE_OUT}`;
      currentStep.style.opacity    = '0';
      currentStep.style.transform  = 'translateX(8px)';

      setTimeout(() => {
        currentStep.setAttribute('hidden', '');
        currentStep.style.opacity   = '';
        currentStep.style.transform = '';
        prevStep.removeAttribute('hidden');
        prevStep.style.opacity   = '0';
        prevStep.style.transform = 'translateX(-8px)';
        prevStep.style.transition = `opacity 280ms ${EASE_OUT}, transform 280ms ${EASE_OUT}`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            prevStep.style.opacity   = '1';
            prevStep.style.transform = 'translateX(0)';
          });
        });
        const router = prevStep.closest('.jtbd-router');
        if (router && router.style.maxHeight !== 'none') {
          router.style.maxHeight = router.scrollHeight + 'px';
        }
      }, 210);
    });
  });
})();


// ─── ALL TOOLS — FILTER ──────────────────────────────────────
(function initToolsFilter() {
  const cards      = document.querySelectorAll('.tool-card');
  const filterBtns = document.querySelectorAll('.filter-tab');
  const noResults  = document.getElementById('no-results');
  let activeFilter = 'all';

  function applyFilters() {
    let visible = 0;
    cards.forEach(card => {
      const cluster = card.dataset.cluster || '';
      const jtbd    = card.dataset.jtbd || '';
      const matchF  = activeFilter === 'all' ||
        cluster === activeFilter ||
        jtbd.split(' ').includes(activeFilter);
      const show    = matchF;

      if (show) {
        card.classList.remove('hidden-by-filter');
        visible++;
      } else {
        card.classList.add('hidden-by-filter');
      }
    });
    document.querySelectorAll('.edge-cluster').forEach(function (clusterEl) {
      var anyVisible = clusterEl.querySelector('.tool-card:not(.hidden-by-filter)');
      clusterEl.style.display = anyVisible ? '' : 'none';
    });
    if (noResults) {
      noResults.hidden = visible > 0;
    }
  }

  filterBtns.forEach(btn => {
    btn.addEventListener('click', function () {
      filterBtns.forEach(b => {
        b.classList.remove('is-active', 'filter-btn--active', 'active');
        b.setAttribute('aria-pressed', 'false');
      });
      this.classList.add('is-active');
      this.setAttribute('aria-pressed', 'true');
      activeFilter = this.dataset.filter;
      applyFilters();
      logEvent(`filter-${activeFilter}`);
    });
  });
})();


// ─── FAQ ACCORDION ───────────────────────────────────────────
(function initFaq() {
  document.querySelectorAll('.faq-item__q').forEach(btn => {
    btn.addEventListener('click', function () {
      const expanded = this.getAttribute('aria-expanded') === 'true';
      const panel    = document.getElementById(this.getAttribute('aria-controls'));
      if (!panel) return;

      if (expanded) {
        panel.setAttribute('hidden', '');
        this.setAttribute('aria-expanded', 'false');
      } else {
        // Close others
        document.querySelectorAll('.faq-item__q[aria-expanded="true"]').forEach(other => {
          if (other === this) return;
          const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
          if (otherPanel) otherPanel.setAttribute('hidden', '');
          other.setAttribute('aria-expanded', 'false');
        });
        panel.removeAttribute('hidden');
        this.setAttribute('aria-expanded', 'true');
        logEvent('faq-' + (this.id || 'unknown'));
      }
    });
  });
})();


// ─── LIVE ACTIVITY STRIP ─────────────────────────────────────
(function initActivityStrip() {
  const strip     = document.getElementById('activity-strip');
  const statBids  = document.getElementById('stat-bids');
  const statRfps  = document.getElementById('stat-rfps');
  const statRevs  = document.getElementById('stat-reviews');
  if (!strip) return;

  /**
   * Tries to fetch anonymized counters from the Opora backend.
   * Falls back to hiding the strip gracefully — NEVER shows fake data.
   *
   * Replace this URL with your actual counter endpoint.
   * Expected JSON: { bids: number, rfps: number, reviews: number }
   */
  const COUNTER_URL = '/api/hub-counters';

  function animateCounter(el, target) {
    if (!el) return;
    const duration = 800;
    const start    = Date.now();
    const from     = 0;

    function tick() {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.round(from + (target - from) * eased).toLocaleString();
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  fetch(COUNTER_URL, { cache: 'no-store' })
    .then(r => {
      if (!r.ok) throw new Error('counter fetch failed');
      return r.json();
    })
    .then(data => {
      if (typeof data.bids !== 'number') throw new Error('malformed response');
      strip.removeAttribute('hidden');
      animateCounter(statBids,  data.bids);
      animateCounter(statRfps,  data.rfps);
      animateCounter(statRevs,  data.reviews);
    })
    .catch(() => {
      // Silently hide — never show fake numbers
      strip.setAttribute('hidden', '');
    });
})();


// ─── AI EVERYWHERE DIAGRAM — animated arrow on scroll ────────
(function initFlowArrows() {
  const arrows = document.querySelectorAll('.flow-arrow');
  if (!arrows.length) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Stagger arrows
        const allArrows = document.querySelectorAll('.flow-arrow');
        allArrows.forEach((arrow, i) => {
          setTimeout(() => arrow.classList.add('animated'), i * 300);
        });
        io.disconnect();
      }
    });
  }, { threshold: 0.4 });

  const section = document.querySelector('.ai-everywhere');
  if (section) io.observe(section);
})();


// ─── STICKY MOBILE CTA — show after hero ─────────────────────
(function initStickyMobileCta() {
  const cta  = document.getElementById('sticky-mobile-cta');
  const hero = document.querySelector('.hero');
  if (!cta || !hero) return;

  // Only on mobile (max 640px)
  const mq = matchMedia('(max-width: 640px)');
  if (!mq.matches) return;

  // Show after user scrolls past hero
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) {
        cta.style.display = 'block';
        // Animate in
        cta.style.transform = 'translateY(80px)';
        cta.style.opacity   = '0';
        cta.style.transition = `transform 350ms ${EASE_OUT}, opacity 350ms ${EASE_OUT}`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            cta.style.transform = 'translateY(0)';
            cta.style.opacity   = '1';
          });
        });
      } else {
        cta.style.transform = 'translateY(80px)';
        cta.style.opacity   = '0';
        setTimeout(() => { if (cta.style.opacity === '0') cta.style.display = 'none'; }, 360);
      }
    });
  }, { threshold: 0.1 });

  io.observe(hero);
})();


// ─── JTBD tile — keyboard hover glow (accessibility) ────────
(function initTileFocusGlow() {
  document.querySelectorAll('.jtbd-tile__header').forEach(header => {
    header.addEventListener('focus', () => {
      header.closest('.jtbd-tile').style.borderColor = 'var(--primary)';
    });
    header.addEventListener('blur', () => {
      if (header.getAttribute('aria-expanded') !== 'true') {
        header.closest('.jtbd-tile').style.borderColor = '';
      }
    });
  });
})();


// ─── FILTER buttons — JTBD tiles cross-link ─────────────────
// If the user clicks a JTBD tile, also highlight the matching filter
(function syncJtbdToFilter() {
  document.querySelectorAll('.jtbd-tile__header').forEach(header => {
    header.addEventListener('click', () => {
      const jtbd = header.closest('.jtbd-tile').dataset.jtbd;
      if (!jtbd) return;
      // Don't change the active filter — this just signals the tile was used
      logEvent(`jtbd-tile-${jtbd}`);
    });
  });
})();


// ─── ROUTER OPTIONS — log clicks ────────────────────────────
document.querySelectorAll('.router__option').forEach(opt => {
  opt.addEventListener('click', function () {
    logEvent(this.dataset.log || 'router-option');
  });
});


// ─── INTERSECTION: lazy-load below-fold sections ────────────
(function initLazyReveal() {
  // Modern browsers support scroll-driven CSS animations natively.
  // This JS fallback handles browsers that don't (Safari < 17, Firefox < 110).
  if (CSS.supports('animation-timeline', 'scroll()')) return;

  const targets = document.querySelectorAll('.fade-in');
  if (!targets.length) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.style.transition = `opacity 500ms ${EASE_OUT}`;
        entry.target.style.opacity    = '1';
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  targets.forEach(el => {
    el.style.opacity = '0';
    io.observe(el);
  });
})();


// ─── HERO SCROLL HINT — hide on first scroll ────────────────
(function hideScrollHint() {
  const hint = document.getElementById('scroll-hint');
  if (!hint) return;
  function handleScroll() {
    if (window.scrollY > 100) {
      hint.style.transition = 'opacity 300ms';
      hint.style.opacity    = '0';
      window.removeEventListener('scroll', handleScroll);
    }
  }
  window.addEventListener('scroll', handleScroll, { passive: true });
})();
